import axios, { type AxiosRequestConfig } from 'axios'
import { toast } from 'vue-sonner'
import router from '@/router'
import { useAuthStore } from '@/modules/auth'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 45000,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

type AxiosErrorLike = {
  code?: string
  response?: {
    status?: number
    data?: unknown
  }
}

function readErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined
  const message = (data as { message?: unknown }).message
  return typeof message === 'string' && message.trim() ? message : undefined
}

/** File downloads use `responseType: 'blob'`, so JSON error bodies arrive as Blobs. */
async function normalizeAxiosErrorData(error: AxiosErrorLike): Promise<void> {
  const data = error.response?.data
  if (!(data instanceof Blob)) return

  const contentType = data.type || ''
  if (contentType && !contentType.includes('json') && !contentType.includes('text')) {
    return
  }

  try {
    const text = await data.text()
    if (!text) return
    error.response!.data = JSON.parse(text)
  } catch {
    // Keep the original blob when the body is not JSON.
  }
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type']
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosErrorLike) => {
    await normalizeAxiosErrorData(error)

    const status = error.response?.status

    if (!status) {
      if (!axios.isCancel(error)) {
        if (error.code === 'ECONNABORTED') {
          toast.error('The request timed out. Please try again.')
        } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
          toast.error('You appear to be offline. Check your connection and try again.')
        } else {
          toast.error('Unable to reach the server. Please try again.')
        }
      }
      return Promise.reject(error)
    }
    const data = error.response?.data as
      | { message?: string; errors?: Record<string, string[]> }
      | undefined

    if (status === 422) {
      const errors = data?.errors

      if (errors && typeof errors === 'object') {
        Object.values(errors).forEach((fieldErrors) => {
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach((message: string) => {
              toast.error(message)
            })
          }
        })
      } else {
        toast.error(readErrorMessage(data) || 'Validation error')
      }
    }
    if (status === 401) {
      const message = readErrorMessage(data) || 'Unauthorized'
      toast.error(message)

      if (window.location.pathname !== '/login') {
        const authStore = useAuthStore()
        authStore.clearSession()
        setTimeout(() => {
          router.push({ name: 'login' })
        }, 50)
      }
    }

    if (status === 403) {
      setTimeout(() => {
        router.push({ name: 'forbidden' })
      }, 100)
    }

    if (status === 500 || status === 502 || status === 400 || status === 404 || status === 419) {
      toast.error(readErrorMessage(data) || 'Something went wrong!')
    }
    return Promise.reject(error)
  }
)

export const get = <T = unknown>(url: string, config?: AxiosRequestConfig) =>
  api.get<T>(url, config)

export const post = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
  api.post<T>(url, data, config)

export const put = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
  api.put<T>(url, data, config)

export const patch = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
  api.patch<T>(url, data, config)

export const del = <T = unknown>(url: string, config?: AxiosRequestConfig) =>
  api.delete<T>(url, config)

export default api
